/* eslint-disable no-restricted-imports */
import { useState } from 'react';

import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Cancel,
  CheckCircle,
  HourglassEmpty,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
  Warning,
} from '@material-ui/icons';

import { FormatDate, FormatMoney } from 'components';

function InvoiceRow({ invoice }) {
  const [open, setOpen] = useState(false);

  const getStatusIcon = (status) => {
    if (status === 'AUTHORIZED') return <CheckCircle style={{ color: '#4CAF50' }} />;
    if (status === 'CANCELLED') return <Cancel style={{ color: '#757575' }} />;
    if (status === 'ERROR') return <Warning style={{ color: '#F44336' }} />;
    if (status === 'PENDING') return <HourglassEmpty style={{ color: '#FF9800' }} />;
    return null;
  };

  const getStatusLabel = (status) => {
    if (status === 'AUTHORIZED') return 'Autorizada';
    if (status === 'CANCELLED') return 'Cancelada';
    if (status === 'ERROR') return 'Erro';
    if (status === 'PENDING') return 'Aguardando Autorização';
    return 'Desconhecido';
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{invoice.invoice_number}</TableCell>
        <TableCell>{invoice.order_number}</TableCell>
        <TableCell>{invoice.customer_name}</TableCell>
        <TableCell>
          <Chip
            icon={getStatusIcon(invoice.status)}
            label={getStatusLabel(invoice.status)}
            size='small'
            style={{
              backgroundColor:
                invoice.status === 'AUTHORIZED'
                  ? '#E8F5E9'
                  : invoice.status === 'CANCELLED'
                  ? '#F5F5F5'
                  : invoice.status === 'PENDING'
                  ? '#FFF3E0'
                  : '#FFEBEE',
            }}
          />
        </TableCell>
        <TableCell>
          <FormatDate>{invoice.created_at}</FormatDate>
        </TableCell>
        <TableCell align='right'>
          <FormatMoney>{invoice.total_value}</FormatMoney>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box margin={2}>
              <Typography variant='h6' gutterBottom>
                Itens da Nota Fiscal
              </Typography>
              {invoice.nfe_key && (
                <Typography variant='caption' color='textSecondary' paragraph>
                  Chave NFe: {invoice.nfe_key}
                </Typography>
              )}
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align='center'>Quantidade</TableCell>
                    <TableCell align='right'>Preço Un.</TableCell>
                    <TableCell align='right'>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.part_number}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={item.quantity}
                          size='small'
                          style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <FormatMoney>{item.unit_price}</FormatMoney>
                      </TableCell>
                      <TableCell align='right'>
                        <FormatMoney>{item.total_price}</FormatMoney>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function InvoicesTab({ data }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredInvoices = data.invoices.filter((invoice) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      invoice.invoice_number.toLowerCase().includes(searchLower) ||
      invoice.order_number.toLowerCase().includes(searchLower) ||
      invoice.customer_name.toLowerCase().includes(searchLower) ||
      invoice.customer_cgc.includes(searchLower) ||
      (invoice.nfe_key && invoice.nfe_key.toLowerCase().includes(searchLower))
    );
  });

  const paginatedInvoices = filteredInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box mb={2}>
        <TextField
          fullWidth
          variant='outlined'
          size='small'
          placeholder='Pesquisar por número da NF, pedido, cliente, CPF/CNPJ ou chave NFe...'
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Nº NF</TableCell>
              <TableCell>Nº Pedido</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data Emissão</TableCell>
              <TableCell align='right'>Valor Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInvoices.length > 0 ? (
              paginatedInvoices.map((invoice) => (
                <InvoiceRow key={invoice.invoice_id} invoice={invoice} />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align='center'>
                  <Typography variant='body2' color='textSecondary'>
                    Nenhuma nota fiscal encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component='div'
          count={filteredInvoices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage='Linhas por página:'
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
}
